import { CircularProgress, Grid2 as Grid } from '@mui/material'
import { fetchWithToken } from 'hooks/http'
import useAuth from 'hooks/useAuth'
import { useFetchOrganizationCoreSettings } from 'hooks/useOrganizations'
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import type { VariableAllocationTable } from 'types/comp-allocations'
import type { Organization, OrganizationCoreSetting } from 'types/organization'
import type { Period } from 'types/periods'
import { createIdToPeriodFromList, createLabelToIdMap } from 'types/periods'
import { Permissions } from 'types/permissions'
import type { UserRole } from 'types/role'
import TemplateReview from '../CreateTemplate/TemplateReview'

const ViewTemplate: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>()
  const navigate = useNavigate()
  const { token, profile, isLoading: isLoadingProfile } = useAuth()

  const [isLoadingTemplate, setIsLoadingTemplate] = useState(true)
  const [label, setLabel] = useState('')
  const [position, setPosition] = useState('')
  const [organizationIds, setOrganizationIds] = useState<string[]>([])
  const [periodId, setPeriodId] = useState('')
  const [compensationModel, setCompensationModel] = useState<string>('BASE_PLUS')
  const [variablePercent, setVariablePercent] = useState(0.1)
  const [minVariablePercent, setMinVariablePercent] = useState(0.05)
  const [maxVariablePercent, setMaxVariablePercent] = useState(0.15)
  const [period, setPeriod] = useState<Period | null>(null)
  const [allocationTables, setAllocationTables] = useState<VariableAllocationTable[]>([])
  const [exampleSalary, setExampleSalary] = useState<number>(100000)
  const [defaultVariableCap, setDefaultVariableCap] = useState<number>(0.1)
  const [minVariableCap, setMinVariableCap] = useState<number>(0.05)
  const [maxVariableCap, setMaxVariableCap] = useState<number>(0.15)
  const [currency, setCurrency] = useState<string>('USD')
  const [currencyDecimals, setCurrencyDecimals] = useState<number>(2)
  const [coreSettings, setCoreSettings] = useState<OrganizationCoreSetting | null>(null)
  const { coreSettings: fetchedCoreSettings, isLoading: isLoadingCoreSettings } = useFetchOrganizationCoreSettings(
    organizationIds.length > 0 ? [organizationIds[0]] : [],
    token!
  )
  const [initializedFromCore, setInitializedFromCore] = useState(false)

  useEffect(() => {
    if (templateId) {
      setIsLoadingTemplate(true)
      fetchWithToken(`compensation/templates/${templateId}`, token!, null)
        .then((data) => {
          setLabel(data.template.label)
          setPosition(data.template.position)
          setOrganizationIds(data.template.organizationIds)
          setPeriodId(data.template.periodId)
          setCompensationModel(data.template.compensationModel)
          setVariablePercent(data.template.variablePercent)
          setMinVariablePercent(data.template.minVariablePercent)
          setMaxVariablePercent(data.template.maxVariablePercent)
          setDefaultVariableCap(data.template.variableCap)
          setMinVariableCap(data.template.minVariableCap)
          setMaxVariableCap(data.template.maxVariableCap)
          setAllocationTables(data.template.variableAllocations)
          setExampleSalary(100000)
          if (data.period) {
            const periodMap = createIdToPeriodFromList([data.period])
            const compensationPeriod = periodMap[data.template.periodId]
            setPeriod(compensationPeriod)
          }
          setIsLoadingTemplate(false)
        })
        .catch(() => {
          setIsLoadingTemplate(false)
          navigate('/admin/templates')
        })
    }
  }, [templateId, token, navigate])

  useEffect(() => {
    if (fetchedCoreSettings && fetchedCoreSettings.length > 0) {
      setCoreSettings(fetchedCoreSettings[0])
    }
  }, [fetchedCoreSettings])

  useEffect(() => {
    if (coreSettings && !initializedFromCore) {
      setCurrency(coreSettings.currency)
      setCurrencyDecimals(coreSettings.currencyDecimals)
      setInitializedFromCore(true)
    }
  }, [coreSettings, initializedFromCore])

  const organizationMap: Record<string, Organization> =
    profile?.roles.reduce(
      (acc: Record<string, Organization>, role: UserRole) => {
        if (role.role.permissions.includes(Permissions.PERIODS_READ)) {
          acc[role.organization.id] = role.organization
        }
        return acc
      },
      {} as Record<string, Organization>
    ) || {}

  const periodIdToLabelMap = (period: Period | null) => {
    if (period == null) {
      return {}
    }
    return Object.entries(createLabelToIdMap(period)).reduce(
      (acc, [key, value]) => {
        acc[value] = key
        return acc
      },
      {} as Record<string, string>
    )
  }

  const periodLabels = periodIdToLabelMap(period)

  if (isLoadingProfile || isLoadingTemplate || isLoadingCoreSettings) {
    return (
      <Grid container spacing={12}>
        <Grid
          size={{ xs: 12 }}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <CircularProgress color="primary" />
          Loading
        </Grid>
      </Grid>
    )
  }

  return (
    <TemplateReview
      allocationTables={allocationTables}
      periodIdToLabelMap={periodLabels}
      label={label}
      position={position}
      periodId={periodId}
      compensationModel={compensationModel}
      exampleSalary={exampleSalary}
      variableCapMin={minVariableCap}
      variableCapDefault={defaultVariableCap}
      variableCapMax={maxVariableCap}
      variablePercentMin={minVariablePercent}
      variablePercentMax={maxVariablePercent}
      variablePercentDefault={variablePercent}
      organizationNames={organizationIds.map((id) => organizationMap[id]?.name || '')}
      currency={currency}
      currencyDecimals={currencyDecimals}
      period={period!}
    />
  )
}

export default ViewTemplate
